export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function cssVar(el: Element, name: string, fallback: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

export function computePeaks(buffer: AudioBuffer, buckets: number): { min: Float32Array; max: Float32Array } {
  const channels = buffer.numberOfChannels;
  const length = buffer.length;
  const samplesPerBucket = Math.max(1, Math.floor(length / buckets));
  const min = new Float32Array(buckets);
  const max = new Float32Array(buckets);
  const channelData: Float32Array[] = [];
  for (let c = 0; c < channels; c++) channelData.push(buffer.getChannelData(c));
  for (let b = 0; b < buckets; b++) {
    const start = b * samplesPerBucket;
    const end = Math.min(length, start + samplesPerBucket);
    let mn = 0;
    let mx = 0;
    for (let i = start; i < end; i++) {
      let sum = 0;
      for (let c = 0; c < channels; c++) sum += channelData[c]![i]!;
      const v = sum / channels;
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
    min[b] = mn;
    max[b] = mx;
  }
  return { min, max };
}

export function getAudioContextCtor(): typeof AudioContext {
  return window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
}

// A single, app-wide AudioContext used ONLY to decode audio into waveform peaks — never to play.
// Decoding doesn't need a running context, so this stays open (suspended) for the session. Sharing
// ONE decode context avoids both a per-player live context and the browser's ~6 concurrent-context
// cap when a screen mounts many waveform tiles at once (a card, its preview, the library list…).
// Playback still spins up its own short-lived context, and only for a clip actually being played.
let sharedDecodeCtx: AudioContext | null = null;
export function getDecodeContext(): AudioContext {
  if (!sharedDecodeCtx) sharedDecodeCtx = new (getAudioContextCtor())();
  return sharedDecodeCtx;
}

// Deterministic (same id always produces the same color), purely cosmetic per-asset tint so a
// grid of otherwise-identical audio tiles is easier to tell apart at a glance — carries no
// semantic meaning (unlike the waveform shape or transcript snippet next to it).
export function assetAccentColor(id: string, alpha = 0.16): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  const hue = hash % 360;
  return `hsl(${hue} 70% 50% / ${alpha})`;
}
